/**
 * AI-Enhanced Product Description Controller
 * Handles tabs, accordions, and client-side safety fallback monitoring.
 */

class AiProductDescriptionSection extends HTMLElement {
  constructor() {
    super();
    this.initTabs();
    this.initAccordions();
    this.verifyAndFallbackIfNeeded();
  }

  initTabs() {
    const tabButtons = this.querySelectorAll('.ai-desc-tab-btn');
    const tabPanels = this.querySelectorAll('.ai-desc-tab-panel');

    if (!tabButtons.length) return;

    tabButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = btn.getAttribute('data-tab-target');

        // Update buttons
        tabButtons.forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-selected', 'true');

        // Update panels
        tabPanels.forEach(panel => {
          if (panel.id === targetId) {
            panel.classList.add('active');
          } else {
            panel.classList.remove('active');
          }
        });
      });

      // Keyboard navigation (Arrow keys)
      btn.addEventListener('keydown', (e) => {
        const btns = Array.from(tabButtons);
        const index = btns.indexOf(btn);

        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextBtn = btns[(index + 1) % btns.length];
          nextBtn.focus();
          nextBtn.click();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevBtn = btns[(index - 1 + btns.length) % btns.length];
          prevBtn.focus();
          prevBtn.click();
        }
      });
    });
  }

  initAccordions() {
    const accordions = this.querySelectorAll('.ai-desc-accordion-item details');
    if (!accordions.length) return;

    // Optional single-open accordion behavior if configured
    const singleOpen = this.getAttribute('data-single-open') === 'true';
    if (singleOpen) {
      accordions.forEach(targetDetail => {
        targetDetail.addEventListener('toggle', () => {
          if (targetDetail.open) {
            accordions.forEach(detail => {
              if (detail !== targetDetail && detail.open) {
                detail.open = false;
              }
            });
          }
        });
      });
    }
  }

  /**
   * Accuracy & Reliability Safeguard:
   * Checks if AI content is valid. If corrupted, empty, or flagged with fallback,
   * it seamlessly unhides the original Shopify description and hides the failed AI block.
   */
  verifyAndFallbackIfNeeded() {
    const aiContent = this.querySelector('.ai-desc-ai-content');
    const fallbackContent = this.querySelector('.ai-desc-fallback-content');

    if (!aiContent || !fallbackContent) return;

    const rawText = aiContent.innerText.trim();

    // Trigger fallback if content is empty or contains error/fallback markers
    const hasFallbackFlag = rawText.includes('AI_PROCESSING_FALLBACK');
    const isTooShort = rawText.length < 30;

    if (hasFallbackFlag || isTooShort) {
      console.warn('[AI Description Safeguard] Unreliable or incomplete AI content detected. Reverting automatically to original Shopify description.');
      aiContent.style.display = 'none';
      fallbackContent.style.display = 'block';
      fallbackContent.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('ai-product-description')) {
  customElements.define('ai-product-description', AiProductDescriptionSection);
}
